import React from 'react';
import { FilterAutocompleteEditor } from 'components/design-system/molecules/FilterAutocompleteEditor';
import EquipmentWebAPIUtils from 'utils/EquipmentWebAPIUtils';
/**
 * Declarative filter config for the Equipment list (v2).
 *
 * Mirrors the legacy `equipment/Filters.js` filter groups plus the equipment
 * autocomplete from `equipment/List.js`, translated into the unified
 * design-system FilterConfig contract.
 *
 * `buildQuery(makeEquipmentFilters(), values)` reproduces the params the
 * legacy List sent to `EquipmentActionCreators.getAllEquipment`:
 *   - `equipment` — array of selected equipment ids
 *   - `status` — array of selected statuses
 *   - date-range filters (last free / last cut / earliest empty / earliest
 *     return) which the page assembles into the legacy `dates` payload via
 *     the `toApiParam.transform` defined here (start_date / end_date ISO).
 *
 * NOTE: the legacy `getFiltersWithoutDates` stripped the per-field
 * `*_start_date` / `*_end_date` keys before sending and assembled a nested
 * `dates` object out of band. v2 keeps each date range as a single filter and
 * lets the page fold the transformed `{ start_date, end_date }` shapes under
 * the `dates` key — same wire payload, simpler state.
 */
const STATUS_OPTIONS = [
    { label: 'Available', value: 'available' },
    { label: 'Unavailable', value: 'unavailable' },
    { label: 'Closed Area', value: 'closed_area' },
    { label: 'On Area', value: 'on_area' },
];
/**
 * Date-range filter ids whose serialized values the legacy List nested under
 * a single `dates` payload key (`equipment/List.js#buildFilters` + DATE_FIELDS).
 * Order matters: it is preserved when assembling the `dates` object so the v2
 * payload key ordering matches v1.
 */
export const DATE_RANGE_FIELD_IDS = [
    'last_free_date',
    'last_cut_date',
    'earliest_empty_date',
    'earliest_return_date',
];
/**
 * Serializes a design-system date-range value into the legacy per-field
 * `dates` shape. Mirrors `equipment/List.js#buildFilters`: when only one
 * bound is present it is used as `start_date`. Returns undefined for an
 * empty range so buildQuery omits it.
 */
export const toDateRangeParam = (v) => {
    if (!v || typeof v !== 'object')
        return undefined;
    const range = v;
    const start = range.from instanceof Date ? range.from : null;
    const end = range.to instanceof Date ? range.to : null;
    if (!start && !end)
        return undefined;
    const out = {};
    if (start) {
        out.start_date = start.toISOString();
        if (end)
            out.end_date = end.toISOString();
    }
    else if (end) {
        out.start_date = end.toISOString();
    }
    return out;
};
/**
 * Assembles the legacy nested `dates` payload from the raw date-range filter
 * values, applying `toDateRangeParam` per field (incl. the single-bound rule:
 * a lone end date is sent as `start_date`).
 *
 * Folding happens HERE — from raw `values` rather than from a built query —
 * because the shared `buildQuery#isEmpty` treats a single-bound date range
 * (only `from`, or only `to`) as empty and omits it. The legacy
 * `equipment/List.js#buildFilters` intentionally SENT single-bound ranges, so
 * reproducing v1's payload requires serializing the date ranges independently
 * of buildQuery. Returns `{}` when no date filter has any bound (no `dates`
 * key), matching v1 which only set `filters.dates` keys for non-empty ranges.
 */
export function buildEquipmentDatesPayload(values) {
    const dates = {};
    for (const field of DATE_RANGE_FIELD_IDS) {
        const serialized = toDateRangeParam(values[field]);
        if (serialized !== undefined)
            dates[field] = serialized;
    }
    return Object.keys(dates).length > 0 ? { dates } : {};
}
export function makeEquipmentFilters() {
    return [
        // Equipment autocomplete — mirrors the legacy EquipmentSearch organism but
        // uses the shared design-system editor + EquipmentWebAPIUtils.searchEquipment.
        {
            id: 'equipment',
            label: 'Equipment',
            kind: 'autocomplete-multi',
            initialValue: [],
            toApiParam: { name: 'equipment' },
            render: (ctx) => (React.createElement(FilterAutocompleteEditor, { onSearch: async (query) => {
                    var _a, _b;
                    const resp = await EquipmentWebAPIUtils.searchEquipment({
                        equipment_id: query,
                    });
                    return ((_b = (_a = resp === null || resp === void 0 ? void 0 : resp.entity) === null || _a === void 0 ? void 0 : _a.items) !== null && _b !== void 0 ? _b : []);
                }, toItem: (e) => ({
                    id: e._id,
                    label: e.equipment_id || e._id,
                }), value: ctx.value || [], labels: ctx.labels, onAdd: ctx.onAdd, onRemove: ctx.onRemove, placeholder: "Search for equipment..." })),
        },
        {
            id: 'status',
            label: 'Status',
            kind: 'select-multi',
            initialValue: [],
            options: STATUS_OPTIONS,
            toApiParam: { name: 'status' },
        },
        {
            id: 'last_free_date',
            label: 'Last Free Date',
            kind: 'date-range',
            initialValue: null,
            toApiParam: { name: 'last_free_date', transform: toDateRangeParam },
        },
        {
            id: 'last_cut_date',
            label: 'Last Cut Date',
            kind: 'date-range',
            initialValue: null,
            toApiParam: { name: 'last_cut_date', transform: toDateRangeParam },
        },
        {
            id: 'earliest_empty_date',
            label: 'Earliest Date to Get Empty',
            kind: 'date-range',
            initialValue: null,
            toApiParam: { name: 'earliest_empty_date', transform: toDateRangeParam },
        },
        {
            id: 'earliest_return_date',
            label: 'Earliest Return Date',
            kind: 'date-range',
            initialValue: null,
            toApiParam: { name: 'earliest_return_date', transform: toDateRangeParam },
        },
    ];
}
